import React, { createContext, useState, useEffect } from 'react';
import { getVariantPrice } from '../utils/productPrice';

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    try {
      const item = window.localStorage.getItem('marybe_cart');
      return item ? JSON.parse(item) : [];
    } catch (error) {
      console.warn("Error reading localStorage", error);
      return [];
    }
  });

  const [appliedGiftCard, setAppliedGiftCard] = useState(() => {
    try {
      const item = window.localStorage.getItem('marybe_giftcard');
      return item ? JSON.parse(item) : null;
    } catch (error) {
      return null;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem('marybe_cart', JSON.stringify(cartItems));
    } catch (error) {
      console.warn("Error setting localStorage", error);
    }
  }, [cartItems]);

  useEffect(() => {
    try {
      if (appliedGiftCard) {
        window.localStorage.setItem('marybe_giftcard', JSON.stringify(appliedGiftCard));
      } else {
        window.localStorage.removeItem('marybe_giftcard');
      }
    } catch (error) {
      console.warn("Error setting giftcard to localStorage", error);
    }
  }, [appliedGiftCard]);

  const addToCart = (product, quantity = 1, variant = null) => {
    const attrs = product.attributes || product;
    const giftCard = product.id?.toString().startsWith('gift-card-') || attrs.nombre?.toLowerCase().includes('gift card');
    const available = giftCard ? 99 : Number(variant?.stock ?? attrs.stock ?? 99);
    const requested = Math.min(Math.floor(Number(quantity)), available);
    if (!Number.isFinite(requested) || requested < 1) return;
    setCartItems(prevItems => {
      // Create a unique key for the cart item based on product id, size and color
      const sizeStr = variant?.volumen || 'Único';
      const colorStr = variant?.color_nombre || 'Único';
      const productId = product.id || product.documentId || product.attributes?.id || product.attributes?.documentId;
      const variantId = variant?.id || variant?.documentId || `${sizeStr}-${colorStr}`;
      const uniqueId = `${productId}-${variantId}`;

      const existingItemIndex = prevItems.findIndex(item => item.cartId === uniqueId);

      if (existingItemIndex >= 0) {
        // Item exists, update quantity
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex] = { ...updatedItems[existingItemIndex], quantity: Math.min(available, Number(updatedItems[existingItemIndex].quantity) + requested) };
        
        // Ensure we don't exceed stock if available
        const isGiftCard = product.id?.toString().startsWith('gift-card-') || product.nombre?.toLowerCase().includes('gift card');
        const maxStock = isGiftCard ? 99 : (variant?.stock ?? product.stock ?? product.attributes?.stock ?? 99);
        if (updatedItems[existingItemIndex].quantity > maxStock) {
           updatedItems[existingItemIndex].quantity = maxStock;
        }
        return updatedItems;
      } else {
        // Add new item
        const priceInfo = getVariantPrice(variant, product.attributes || product);
        const unitPrice = priceInfo.tieneOferta ? priceInfo.offerPrice : priceInfo.price;

        const newItem = {
          cartId: uniqueId,
          product: {
            id: product.id || product.documentId,
            nombre: product.nombre || product.attributes?.nombre,
            marca: product.marca || product.attributes?.marca,
            descuento: priceInfo.calcDescuento,
            portada: product.portada || product.attributes?.portada,
            stock: product.stock || product.attributes?.stock, // Added stock
          },
          variant: variant || {},
          quantity: requested,
          price: unitPrice,
        };
        return [...prevItems, newItem];
      }
    });
  };

  const removeFromCart = (cartId) => {
    setCartItems(prevItems => prevItems.filter(item => item.cartId !== cartId));
  };

  const updateQuantity = (cartId, quantity) => {
    setCartItems(prevItems => prevItems.map(item => {
      if (item.cartId === cartId) {
        const isGiftCard = item.product?.id?.toString().startsWith('gift-card-') || item.product?.nombre?.toLowerCase().includes('gift card');
        const maxStock = isGiftCard ? 99 : (item.variant?.stock ?? item.product?.stock ?? 99);
        const newQuantity = Math.min(Math.max(1, quantity), maxStock);
        return { ...item, quantity: newQuantity }; // Enforce max stock
      }
      return item;
    }));
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (Number(item.price) || 0) * (Number(item.quantity) || 0), 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + Number(item.quantity), 0);
  };

  return (
    <CartContext.Provider value={{
      cartItems,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      cartTotal: getCartTotal(),
      itemCount: getCartCount(),
      appliedGiftCard,
      setAppliedGiftCard,
    }}>
      {children}
    </CartContext.Provider>
  );
};
